import React, { useState, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, CircularProgress,
  Alert, Card, CardContent, Grid, TextField, InputAdornment,
  Checkbox, Snackbar, Tabs, Tab, IconButton, Tooltip
} from '@mui/material';
import {
  Search as SearchIcon,
  Phone as PhoneIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  PersonSearch as PersonSearchIcon
} from '@mui/icons-material';
import { whatsappApi } from '../../services/api';
import ResponsiveHeader from '../../components/ResponsiveHeader';
import ResponsiveFilters from '../../components/ResponsiveFilters';
import MobileCardList from '../../components/MobileCardList';
import { useMobile } from '../../hooks/useMobile';

const PhoneMatching = () => {
  const { isMobile } = useMobile();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState(0);
  const [selected, setSelected] = useState([]);
  const [linking, setLinking] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchMatching = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await whatsappApi.getPhoneMatching();
      setData(res.data);
      setSelected([]);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleAutoLink = async () => {
    if (selected.length === 0) return;
    setLinking(true);
    try {
      const matches = filteredResults
        .filter((r) => selected.includes(r.phone_number) && r.customer?.id)
        .map((r) => ({ phone_number: r.phone_number, customer_id: r.customer.id }));

      if (matches.length === 0) {
        setSnackbar({
          open: true,
          message: 'Nessun match valido selezionato.',
          severity: 'warning'
        });
        setLinking(false);
        return;
      }

      const res = await whatsappApi.autoLinkMatches(matches);
      setSnackbar({
        open: true,
        message: `${res.data.linked} conversazioni collegate con successo!`,
        severity: 'success'
      });
      setSelected([]);
      fetchMatching();
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Errore: ${err.response?.data?.error || err.message}`,
        severity: 'error'
      });
    } finally {
      setLinking(false);
    }
  };

  const results = data?.results || [];
  const stats = data?.stats || {};

  const getFilteredByTab = () => {
    switch (tab) {
      case 0: return results; // Tutti
      case 1: return results.filter(r => r.status === 'match_found');
      case 2: return results.filter(r => r.status === 'linked');
      case 3: return results.filter(r => r.status === 'no_match');
      default: return results;
    }
  };

  const filteredResults = getFilteredByTab().filter(r => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      r.phone_number?.toLowerCase().includes(s) ||
      r.contact_name?.toLowerCase().includes(s) ||
      r.customer?.nome?.toLowerCase().includes(s) ||
      r.customer?.cod_cli?.toLowerCase().includes(s)
    );
  });

  const matchFoundResults = filteredResults.filter(r => r.status === 'match_found');
  const selectablePhones = filteredResults
    .filter((r) => r.status === 'match_found')
    .map((r) => r.phone_number);

  const allSelectableSelected = selectablePhones.length > 0 &&
    selectablePhones.every((phoneNumber) => selected.includes(phoneNumber));

  const handleSelectAll = () => {
    if (allSelectableSelected) {
      setSelected([]);
    } else {
      setSelected(selectablePhones);
    }
  };

  const toggleSelect = (phoneNumber) => {
    setSelected(prev =>
      prev.includes(phoneNumber) ? prev.filter((value) => value !== phoneNumber) : [...prev, phoneNumber]
    );
  };

  const statusChip = (status) => {
    switch (status) {
      case 'linked':
        return <Chip icon={<CheckCircleIcon />} label="Collegato" color="success" size="small" />;
      case 'match_found':
        return <Chip icon={<WarningIcon />} label="Match trovato" color="warning" size="small" />;
      case 'no_match':
        return <Chip icon={<LinkOffIcon />} label="Nessun match" color="default" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const matchFieldLabel = (field) => {
    switch (field) {
      case 'customer_id': return 'Collegamento diretto';
      case 'whatsapp_number': return 'N. WhatsApp';
      case 'cellulare': return 'Cellulare';
      case 'telefono': return 'Telefono';
      default: return field;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <Box sx={{ p: { xs: 0, sm: 2, md: 3 } }}>
      <ResponsiveHeader
        title="Matching Telefoni - WhatsApp"
        subtitle="Incrocia i numeri di telefono dei clienti con le chat WhatsApp"
        primaryAction={{
          label: data ? 'Aggiorna' : 'Avvia Analisi',
          icon: loading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />,
          onClick: fetchMatching,
          disabled: loading,
        }}
      >
        <PersonSearchIcon sx={{ fontSize: 24, color: 'primary.main' }} />
      </ResponsiveHeader>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                  <Typography variant="h4" fontWeight={700} color="primary">{stats.total_chats}</Typography>
                  <Typography variant="body2" color="text.secondary">Chat WA Totali</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                  <Typography variant="h4" fontWeight={700} color="success.main">{stats.linked}</Typography>
                  <Typography variant="body2" color="text.secondary">Già Collegati</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                  <Typography variant="h4" fontWeight={700} color="warning.main">{stats.match_found}</Typography>
                  <Typography variant="body2" color="text.secondary">Match Trovati</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                  <Typography variant="h4" fontWeight={700} color="text.disabled">{stats.no_match}</Typography>
                  <Typography variant="body2" color="text.secondary">Senza Match</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                  <Typography variant="h4" fontWeight={700}>{stats.total_customers_with_phone}</Typography>
                  <Typography variant="body2" color="text.secondary">Clienti con Telefono</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Paper sx={{ mb: 2, p: { xs: 1.5, sm: 2 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, mb: 2 }}>
              <Tabs value={tab} onChange={(e, v) => { setTab(v); setSelected([]); }} variant={isMobile ? 'scrollable' : 'standard'} scrollButtons={isMobile ? 'auto' : false} allowScrollButtonsMobile>
                <Tab label={`Tutti (${results.length})`} />
                <Tab label={`Match trovati (${results.filter(r => r.status === 'match_found').length})`} />
                <Tab label={`Collegati (${results.filter(r => r.status === 'linked').length})`} />
                <Tab label={`Senza match (${results.filter(r => r.status === 'no_match').length})`} />
              </Tabs>
              <Box sx={{ width: { xs: '100%', sm: 300 } }}>
                <ResponsiveFilters
                  searchValue={search}
                  onSearchChange={setSearch}
                  searchPlaceholder="Cerca numero, nome, codice..."
                />
              </Box>
            </Box>

            {selected.length > 0 && (
              <Alert
                severity="info"
                sx={{ mb: 2 }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    startIcon={linking ? <CircularProgress size={16} /> : <LinkIcon />}
                    onClick={handleAutoLink}
                    disabled={linking}
                  >
                    Collega {selected.length} selezionati
                  </Button>
                }
              >
                {selected.length} match selezionati per il collegamento automatico
              </Alert>
            )}

            {tab === 1 && selectablePhones.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <Button size="small" onClick={handleSelectAll} startIcon={<LinkIcon />}>
                  {allSelectableSelected ? 'Deseleziona tutti' : 'Seleziona tutti'}
                </Button>
              </Box>
            )}

            {isMobile ? (
              <MobileCardList
                rows={filteredResults}
                loading={loading}
                idField="phone_number"
                emptyMessage="Nessun risultato"
                renderCard={(r) => (
                  <Card
                    variant="outlined"
                    sx={{
                      borderColor: selected.includes(r.phone_number) ? 'primary.main' : undefined,
                      bgcolor: r.status === 'match_found' ? 'warning.50' : r.status === 'linked' ? 'success.50' : undefined,
                    }}
                  >
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                        {tab === 1 && (
                          <Box sx={{ pt: 0.25 }}>
                            {r.status === 'match_found' ? (
                              <Checkbox
                                size="small"
                                checked={selected.includes(r.phone_number)}
                                onChange={() => toggleSelect(r.phone_number)}
                              />
                            ) : (
                              <Checkbox size="small" disabled checked={false} />
                            )}
                          </Box>
                        )}
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 0.75 }}>
                            {statusChip(r.status)}
                            {r.customer?.cod_cli && <Chip label={r.customer.cod_cli} size="small" variant="outlined" />}
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PhoneIcon fontSize="small" color="action" />
                            <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
                              {r.phone_number}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }} noWrap>
                            Contatto WA: {r.contact_name || '-'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                            Messaggi: {r.total_messages} · Ultimo: {formatDate(r.last_message_at)}
                          </Typography>
                          <Typography variant="body2" fontWeight={r.customer ? 600 : 400} sx={{ mt: 1 }} noWrap>
                            {r.customer?.nome || 'Cliente non collegato'}
                          </Typography>
                          {r.customer?.match_field && (
                            <Tooltip title={r.customer.match_value || ''}>
                              <Chip
                                label={matchFieldLabel(r.customer.match_field)}
                                size="small"
                                color={r.customer.match_field === 'customer_id' ? 'success' : 'info'}
                                variant="outlined"
                                sx={{ mt: 0.75 }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                )}
              />
            ) : (
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {tab === 1 && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={allSelectableSelected}
                            indeterminate={selected.length > 0 && !allSelectableSelected}
                            onChange={handleSelectAll}
                          />
                        </TableCell>
                      )}
                      <TableCell>Stato</TableCell>
                      <TableCell>Numero WA</TableCell>
                      <TableCell>Contatto WA</TableCell>
                      <TableCell>Messaggi</TableCell>
                      <TableCell>Ultimo msg</TableCell>
                      <TableCell>Cliente</TableCell>
                      <TableCell>Cod. Cliente</TableCell>
                      <TableCell>Corrispondenza</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredResults.map((r) => (
                      <TableRow
                        key={r.phone_number}
                        sx={{
                          bgcolor: r.status === 'match_found' ? 'warning.50' :
                                   r.status === 'linked' ? 'success.50' : undefined,
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        {tab === 1 && (
                          <TableCell padding="checkbox">
                            {r.status === 'match_found' && (
                              <Checkbox
                                checked={selected.includes(r.phone_number)}
                                onChange={() => toggleSelect(r.phone_number)}
                              />
                            )}
                          </TableCell>
                        )}
                        <TableCell>{statusChip(r.status)}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PhoneIcon fontSize="small" color="action" />
                            <Typography variant="body2" fontFamily="monospace">{r.phone_number}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{r.contact_name || '-'}</TableCell>
                        <TableCell align="center">{r.total_messages}</TableCell>
                        <TableCell>{formatDate(r.last_message_at)}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={r.customer ? 600 : 400}>
                            {r.customer?.nome || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {r.customer?.cod_cli ? (
                            <Chip label={r.customer.cod_cli} size="small" variant="outlined" />
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {r.customer?.match_field ? (
                            <Tooltip title={r.customer.match_value || ''}>
                              <Chip
                                label={matchFieldLabel(r.customer.match_field)}
                                size="small"
                                color={r.customer.match_field === 'customer_id' ? 'success' : 'info'}
                                variant="outlined"
                              />
                            </Tooltip>
                          ) : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredResults.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={tab === 1 ? 9 : 8} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">Nessun risultato</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </>
      )}

      {!data && !loading && (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <PersonSearchIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Analisi Matching Telefoni
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Premi "Avvia Analisi" per incrociare tutti i numeri di telefono dei clienti<br />
            con le conversazioni WhatsApp e trovare le corrispondenze.
          </Typography>
        </Paper>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PhoneMatching;
